import { useState, useEffect } from "react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalendarBlank, X } from "@phosphor-icons/react";

/**
 * DateTimePicker — replaces native <input type="datetime-local"> with a real
 * calendar + separate time input. Issues an ISO string back to the parent
 * (`onChange(iso | "")`).
 *
 * Why we don't ship the native input:
 *   - Browser styling is inconsistent and ugly on dark themes.
 *   - The `mm/dd/yyyy --:-- --` placeholder leaks through Chrome's autofill
 *     and confuses operators.
 *   - The native picker doesn't let us anchor a popover above other modals.
 *
 * The output is always emitted as ISO 8601 (`YYYY-MM-DDTHH:MM`), which the
 * backend already accepts on shipment ETA / departure / arrival fields.
 */
export default function DateTimePicker({
  value,                  // ISO string or "" — controlled
  onChange,
  placeholder = "Pick a date & time",
  testId = "date-time-picker",
  disabled = false,
}) {
  // Local mirror of the parsed value so the calendar / time input stay in sync.
  const [parsed, setParsed]     = useState(value ? new Date(value) : null);
  const [timePart, setTimePart] = useState(value ? value.slice(11, 16) : "");
  const [open, setOpen]         = useState(false);

  // Keep parsed in sync when parent resets value externally (e.g. dialog close).
  useEffect(() => {
    if (!value) { setParsed(null); setTimePart(""); return; }
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) {
      setParsed(d);
      setTimePart(value.slice(11, 16));
    }
  }, [value]);

  const emit = (date, time) => {
    if (!date) { onChange?.(""); return; }
    const t = time || "09:00";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    onChange?.(`${y}-${m}-${d}T${t}`);
  };

  const onDay = (day) => {
    setParsed(day);
    emit(day, timePart || "09:00");
    if (!timePart) setTimePart("09:00");
  };
  const onTime = (e) => {
    setTimePart(e.target.value);
    if (parsed) emit(parsed, e.target.value);
  };
  const clear = () => { setParsed(null); setTimePart(""); onChange?.(""); setOpen(false); };

  const label = parsed
    ? `${format(parsed, "MMM d, yyyy")}${timePart ? `  ·  ${timePart}` : ""}`
    : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" disabled={disabled}
          data-testid={testId}
          className={`w-full justify-start font-normal bg-zinc-900 border-zinc-800 hover:bg-zinc-800 hover:border-teal-500/40 ${
            parsed ? "text-zinc-100" : "text-zinc-500"
          }`}>
          <CalendarBlank size={14} className="mr-2 text-teal-400 flex-shrink-0" />
          <span className="flex-1 text-left truncate">{label}</span>
          {parsed && !disabled && (
            <button type="button"
              onClick={(e) => { e.stopPropagation(); clear(); }}
              className="text-zinc-500 hover:text-zinc-200 ml-2"
              data-testid={`${testId}-clear`}>
              <X size={12} />
            </button>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 bg-zinc-900 border-zinc-800 z-[2200]"
        align="start" sideOffset={6}>
        <Calendar
          mode="single"
          selected={parsed || undefined}
          onSelect={onDay}
          initialFocus
          className="rounded-md"
        />
        <div className="border-t border-zinc-800 p-3 flex items-center gap-2">
          <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-mono">Time</label>
          <Input type="time" value={timePart} onChange={onTime}
            className="bg-zinc-950 border-zinc-800 text-zinc-100 text-xs h-8 w-32"
            data-testid={`${testId}-time`} />
          <div className="ml-auto flex items-center gap-2">
            <Button type="button" size="sm" variant="ghost"
              onClick={clear}
              className="text-zinc-400 hover:text-zinc-200 text-xs h-7"
              data-testid={`${testId}-clear-btn`}>
              Clear
            </Button>
            <Button type="button" size="sm"
              onClick={() => setOpen(false)}
              className="bg-teal-500 hover:bg-teal-400 text-zinc-950 text-xs h-7"
              data-testid={`${testId}-done`}>
              Done
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
